import { useCallback, useEffect, useRef, useState } from "react";
import {
  FILE_TRANSFER_PROMO_CAPTION_MS,
  FILE_TRANSFER_PROMO_HOLD_MS,
  FILE_TRANSFER_PROMO_LANDING_MS,
  FILE_TRANSFER_PROMO_PHASES,
  FILE_TRANSFER_PROMO_SERVING_MS,
} from "./fileTransferPromo.constants";
import {
  hasSeenFileTransferPromo,
  markFileTransferPromoSeen,
} from "./fileTransferPromo.storage";
import {
  isBlankPromoContent,
  isBlankPromoText,
  isDesktopFileTransferPromoViewport,
  isFileTransferPromoEligible,
} from "./fileTransferPromo.rules";

const HIDDEN_PROMO_STATE = {
  isVisible: false,
  phase: FILE_TRANSFER_PROMO_PHASES.HIDDEN,
};

const shouldStartPromo = ({ isAllowedRoute, noteContent, noteTitle }) => {
  const hasSeenPromo = hasSeenFileTransferPromo();
  const isDesktop = isDesktopFileTransferPromoViewport();

  return isFileTransferPromoEligible({
    hasAllowedRoute: isAllowedRoute,
    hasSeenPromo,
    isDesktop,
    noteContent,
    noteTitle,
  });
};

const showPromo = ({ hidePromo, setPromoState, timerRef }) => {
  markFileTransferPromoSeen();
  setPromoState({
    isVisible: true,
    phase: FILE_TRANSFER_PROMO_PHASES.SERVING,
  });
  startPromoTimeline({ hidePromo, setPromoState, timerRef });
};

const useStartPromoEffect = ({
  clearPromoTimer,
  hasStartedRef,
  hidePromo,
  isAllowedRoute,
  noteContent,
  noteTitle,
  setPromoState,
  timerRef,
}) => {
  useEffect(() => {
    if (hasStartedRef.current) return undefined;
    if (!shouldStartPromo({ isAllowedRoute, noteContent, noteTitle })) {
      return undefined;
    }

    hasStartedRef.current = true;
    showPromo({ hidePromo, setPromoState, timerRef });

    return clearPromoTimer;
  }, [
    clearPromoTimer,
    hasStartedRef,
    hidePromo,
    isAllowedRoute,
    noteContent,
    noteTitle,
    setPromoState,
    timerRef,
  ]);
};

const useDismissPromoEffect = ({
  hidePromo,
  isVisible,
  noteContent,
  noteTitle,
}) => {
  useEffect(() => {
    const isEditing =
      !isBlankPromoText(noteTitle) || !isBlankPromoContent(noteContent);
    if (isVisible && isEditing) hidePromo();
  }, [hidePromo, isVisible, noteContent, noteTitle]);
};

const usePromoControls = () => {
  const [promoState, setPromoState] = useState(HIDDEN_PROMO_STATE);
  const hasStartedRef = useRef(false);
  const timerRef = useRef(null);

  const clearPromoTimer = useCallback(() => {
    if (!timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const hidePromo = useCallback(() => {
    clearPromoTimer();
    setPromoState(HIDDEN_PROMO_STATE);
  }, [clearPromoTimer]);

  return {
    clearPromoTimer,
    hasStartedRef,
    hidePromo,
    promoState,
    setPromoState,
    timerRef,
  };
};

/**
 * @param {{ isAllowedRoute?: boolean, noteContent?: string, noteTitle?: string }} options
 * @returns {{ hidePromo: () => void, isVisible: boolean, phase: string }}
 */
export const useFileTransferPromo = ({
  isAllowedRoute = false,
  noteContent = "",
  noteTitle = "",
} = {}) => {
  const promoControls = usePromoControls();
  const { hidePromo, promoState } = promoControls;

  useStartPromoEffect({
    clearPromoTimer: promoControls.clearPromoTimer,
    hasStartedRef: promoControls.hasStartedRef,
    hidePromo,
    isAllowedRoute,
    noteContent,
    noteTitle,
    setPromoState: promoControls.setPromoState,
    timerRef: promoControls.timerRef,
  });
  useDismissPromoEffect({
    hidePromo,
    isVisible: promoState.isVisible,
    noteContent,
    noteTitle,
  });

  return { ...promoState, hidePromo };
};

const setVisiblePhase = ({ phase, setPromoState }) => {
  setPromoState({ isVisible: true, phase });
};

const startPromoTimeline = ({ hidePromo, setPromoState, timerRef }) => {
  const timeline = [
    [FILE_TRANSFER_PROMO_SERVING_MS, FILE_TRANSFER_PROMO_PHASES.LANDING],
    [FILE_TRANSFER_PROMO_LANDING_MS, FILE_TRANSFER_PROMO_PHASES.CAPTION],
    [FILE_TRANSFER_PROMO_CAPTION_MS, FILE_TRANSFER_PROMO_PHASES.HOLDING],
    [FILE_TRANSFER_PROMO_HOLD_MS, FILE_TRANSFER_PROMO_PHASES.HIDDEN],
  ];

  let timelineIndex = 0;

  const advanceTimeline = () => {
    const [delay, phase] = timeline[timelineIndex];
    timerRef.current = setTimeout(() => {
      if (phase === FILE_TRANSFER_PROMO_PHASES.HIDDEN) {
        hidePromo();
        return;
      }

      setVisiblePhase({ phase, setPromoState });
      timelineIndex += 1;
      advanceTimeline();
    }, delay);
  };

  advanceTimeline();
};
